/**
 * Where a social post stands, in the words a person uses, and the small tags shown on each post.
 *
 * Pure. The stage is worked out from what we already store (our review status, and what Sociamonials told us), so there is
 * one place that decides it. The worker has the same rule as one SQL expression (routes/social.ts `STAGE_SQL`); a test
 * keeps the two in step.
 */
import { z } from "zod";

export const SOCIAL_STAGES = ["draft", "waiting", "approved", "scheduled", "posted", "failed", "cancelled"] as const;
export const socialStageSchema = z.enum(SOCIAL_STAGES);
export type SocialStage = (typeof SOCIAL_STAGES)[number];

export const SOCIAL_STAGE_LABEL: Record<SocialStage, string> = {
  draft: "Draft",
  waiting: "Waiting for review",
  approved: "Love it",
  scheduled: "Scheduled",
  posted: "Posted",
  failed: "Failed to send",
  cancelled: "Cancelled",
};

/** The filter chips on the Posts screen. "attention" is a view across stages, not a stage. */
export const SOCIAL_FILTERS = ["all", "draft", "waiting", "approved", "scheduled", "posted", "attention"] as const;
export const socialFilterSchema = z.enum(SOCIAL_FILTERS);
export type SocialFilter = (typeof SOCIAL_FILTERS)[number];

export interface StageInput {
  status: "draft" | "pending_review" | "approved" | "cancelled";
  smStatus: string | null;
  smPostId: number | null;
  scheduledFor: string | null;
}

/**
 * Order matters: a cancelled post is cancelled whatever Sociamonials says; a failed send shows as failed; "delivered" is
 * Posted; a post Sociamonials is holding is Scheduled; the rest follow our own review status.
 */
export function stageOf(p: StageInput): SocialStage {
  if (p.status === "cancelled") return "cancelled";
  if (p.smStatus === "failed") return "failed";
  if (p.smStatus === "delivered") return "posted";
  if (p.smPostId !== null && p.smStatus !== "canceled") return "scheduled";
  if (p.status === "approved") return "approved";
  if (p.status === "pending_review") return "waiting";
  return "draft";
}

/** A post needs a person when its send failed, or it was approved but its time has passed without being sent. */
export function attentionOf(p: StageInput, now: Date): { attention: boolean; reason: string | null } {
  const stage = stageOf(p);
  if (stage === "failed") return { attention: true, reason: "Failed to send" };
  if (stage === "approved" && p.scheduledFor && Date.parse(p.scheduledFor) < now.getTime()) return { attention: true, reason: "Its time has passed and it was not sent" };
  return { attention: false, reason: null };
}

export type PostFormat = "Text" | "Text + image" | "Video";
export function postFormat(p: { imageUrls: readonly string[]; videoUrl: string | null }): PostFormat {
  if (p.videoUrl) return "Video";
  return p.imageUrls.length > 0 ? "Text + image" : "Text";
}

export type PostHook = "Question" | "Number" | "Statement";
const QUESTION_WORDS = /^(what|why|how|who|when|where|which|are|is|do|does|did|can|could|should|would|will|have|has)\b/i;
/** How the post opens, judged from its first sentence only. */
export function postHook(text: string): PostHook {
  const first = text.trim().match(/^[\s\S]*?(?:[.?!](?=\s|$)|\n|$)/)?.[0]?.trim() ?? "";
  if (/\?$/.test(first) || QUESTION_WORDS.test(first)) return "Question";
  if (/\d/.test(first)) return "Number";
  return "Statement";
}

export const TOPIC_MAX = 40;
/** Short, single-line, trimmed. Null for empty. */
export function cleanTopic(raw: string | null | undefined): string | null {
  const t = (raw ?? "").replace(/\s+/g, " ").trim().slice(0, TOPIC_MAX);
  return t === "" ? null : t;
}

/** The three small chips under a post. A post with no topic simply has no topic chip. */
export function postTags(p: { topic: string | null; imageUrls: readonly string[]; videoUrl: string | null; text: string }): string[] {
  return [...(p.topic ? [`Topic: ${p.topic}`] : []), `Format: ${postFormat(p)}`, `Hook: ${postHook(p.text)}`];
}

export const socialStageCountsSchema = z.object({
  all: z.number().int(),
  draft: z.number().int(),
  waiting: z.number().int(),
  approved: z.number().int(),
  scheduled: z.number().int(),
  posted: z.number().int(),
  failed: z.number().int(),
  cancelled: z.number().int(),
  attention: z.number().int(),
  /** Waiting for review and due within the next 24 hours (or already due). */
  dueUnreviewed: z.number().int(),
  topics: z.array(z.string()),
});
export type SocialStageCounts = z.infer<typeof socialStageCountsSchema>;
